import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import { clone, find, findIndex, move, propEq, remove } from 'ramda';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import * as TrackCard from '@components/LibraryTrackCard';
import * as CourseCard from '@components/LibraryCourseCard';
import * as ModuleCard from '@components/LibraryModuleCard';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';
import { track } from 'react-facebook-pixel';

const TrainingDetail = () => {
    // Setup Hooks
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const location = useLocation();

    // Redux managed State Variables
    const templates = useSelector((state) => state.develop.templates);
    const companyId = useSelector((state) => state.app.company_info.id);

    // Local Consts
    const id = Number(params.id);
    const type = params.activeTab;
    const forwardedTemplate = location.state?.template;
    const template = 
        forwardedTemplate
        || find(propEq('id', id), templates[type]?.data 
        || []
    );
    const search = queryString.parse(location.search)?.filter || null;
    const page = queryString.parse(location.search)?.page || 1;
    const perPage = queryString.parse(location.search)?.per_page || 25;
 
    // Local State Variables
    const [message, setMessage] = useState('Loading....');

    // Initial Load useEffect to fetch template if not already in state
    useEffect(() => {
        if (!template) {
            dispatch(DevelopActions.getlibrarytemplatessearchRequest(type, { id: id, published: [0,1] }));

            setTimeout(() => {
                setMessage(`Template not found.`);
            }, 3000);
        }
    }, []);


    // Event Handlers
    const handleCardMenu = (card) => (event) => {
        switch (event) {
            case 'assign': {
                let payload = {
                    type: 'Assign Training',
                    data: {
                        before: {
                            disabled: type === 'tracks' ? ['courses'] : ['tracks'],
                            modules: [card],
                            companyId,
                        },
                    },
                };
                dispatch(AppActions.modalRequest(payload));
                break;
            }

            case 'edit': {
                const id = card.card_template_id || card.id;
                navigate(`/library/training/${type}/${id}/edit`, {
                    state: { template }
                });
                break;
            }

            case 'preview view':
                if (type === 'tracks') {
                    toast.info('Upcoming Soon.', { position: toast.POSITION.TOP_RIGHT });
                } else if (type === 'courses') {
                    dispatch(
                        AppActions.modalRequest({
                            type: 'Preview',
                            data: { before: { template, module: template.children[0], index: 0 } },
                        })
                    );
                } else if (type === 'modules') {
                    if (template.data.presentation_url && !template.data.presentation_images) {
                        toast.warn('This file is being converted to a presentation.  This process takes a few short minutes.', {
                            position: toast.POSITION.TOP_RIGHT,
                        });
                        return;
                    }
                    dispatch(
                        AppActions.modalRequest({
                            type: 'Preview',
                            data: { before: { course: { children: [template] }, module: template, index: 0 } },
                        })
                    );
                }
                break;
            default:
                break;
        }
    };

    const handleChildMenu = (event, item) => {
        switch (event) {
            case 'detail view':
                handleShowDetail(item, 'view');
                break;
            case 'edit':
                handleShowDetail(item, 'edit');
                break;
            case 'preview view':
                if (type === 'tracks') {
                    dispatch(
                        AppActions.modalRequest({
                            type: 'Preview',
                            data: { before: { course: item, module: item.children[0], index: 0 } },
                        })
                    );
                } else if (type === 'courses') {
                    const index = findIndex(propEq('id', item.id), template.children);
                    if (index > -1) {
                        dispatch(
                            AppActions.modalRequest({
                                type: 'Preview',
                                data: { before: { template, module: { ...item, status: 0 }, index } },
                            })
                        );
                    }
                }
                break;
            case 'quick assign':
                if (type === 'track') {
                    dispatch(
                        AppActions.modalRequest({
                            type: 'Quick Edit',
                            data: {
                                before: {
                                    template: item,
                                    type: 'courses',
                                    from: 'template',
                                    deletable: false,
                                    companyId,
                                },
                            },
                            callBack: { onDelete: () => handleDeleteTemplate(item) },
                        })
                    );
                }
                break;
            case 'move up':
                if (type === 'courses') {
                    const index = findIndex(propEq('id', item.id), template.children);
                    if (index > -1) {
                        let newModules = clone(template.data.modules);
                        let newCourses = clone(template.children);
                        if (index === 0) return;
                        newModules = move(index, index - 1, newModules);
                        newCourses = move(index, index - 1, newCourses);

                        const cards = newModules.map((item, key) => ({
                            blocked_by: key === 0 ? null : newModules[key - 1].card_template_id,
                            card_template_id: item.card_template_id,
                            card_type_id: item.card_type_id,
                            complete_course: 0,
                            delay_days: 0,
                        }));
                        const newCourse = {
                            ...template,
                            children: newCourses,
                            data: {
                                ...template.data,
                                modules: cards,
                                module_count: newModules?.length || 0,
                                thumb_url: template.data.thumbnail,
                            },
                        };
                        const payload = { template: newCourse };
                        const after = {
                            type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
                            target: 'courses',
                            payload: { page, perPage, search },
                        };
                        dispatch(DevelopActions.librarytemplatesaveRequest(payload, null, 'courses', after));
                    }
                }
                if (type === 'tracks') {
                    const index = findIndex(propEq('id', item.id), track.data.cards);
                    if (index > -1) {
                        let newCards = clone(track.data.cards);
                        if (index === 0) return;
                        newCards = move(index, index - 1, newCards);

                        let modules = 0;
                        const cards = newCards.map(({ id, blocked_by, children }) => {
                            modules = modules + children?.length;
                            return {
                                alert_manager: '0',
                                blocked_by,
                                card_template_id: id,
                                card_type: 'course',
                                complete_track: '0',
                            };
                        });
                        const newTrack = {
                            ...track,
                            data: {
                                ...track.data,
                                cards,
                                course_count: cards?.length || 0,
                                module_count: modules,
                                thumb_url: track.thumbnail,
                            },
                        };
                        const payload = { track: newTrack };
                        const after = {
                            type: 'POSTLIBRARYCARDTEMPLATES_REQUEST',
                            target: 'tracks',
                            payload: { data: newTrack },
                        };
                        dispatch(DevelopActions.librarytemplatesaveRequest(payload, null, 'tracks', after));
                    }
                }
                break;

            case 'move down': {
                if (type === 'courses') {
                    const index = findIndex(propEq('id', item.id), template.children);
                    if (index > -1) {
                        let newModules = clone(template.data.modules);
                        let newCourses = clone(template.children);
                        if (newModules?.length === index + 1) return;
                        newModules = move(index, index + 1, newModules);
                        newCourses = move(index, index + 1, newCourses);
                        const cards = newModules.map((item, key) => ({
                            blocked_by: key === 0 ? null : newModules[key - 1].card_template_id,
                            card_template_id: item.card_template_id,
                            card_type_id: item.card_type_id,
                            complete_course: 0,
                            delay_days: 0,
                        }));
                        const newCourse = {
                            ...template,
                            children: newCourses,
                            data: {
                                ...template.data,
                                modules: cards,
                                module_count: newModules?.length || 0,
                                thumb_url: template.data.thumbnail,
                            },
                        };
                        const payload = { template: newCourse };
                        const after = {
                            type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
                            target: 'courses',
                            payload: { page, perPage, search },
                        };
                        dispatch(DevelopActions.librarytemplatesaveRequest(payload, null, 'courses', after));
                    }
                }
                if (type === 'tracks') {
                    const index = findIndex(propEq('id', item.id), track.data.cards);
                    if (index > -1) {
                        let newCards = clone(track.data.cards);
                        if (newCards?.length === index + 1) return;
                        newCards = move(index, index + 1, newCards);
                        let modules = 0;
                        const cards = newCards.map(({ id, blocked_by, children }) => {
                            modules = modules + children?.length;
                            return {
                                alert_manager: '0',
                                blocked_by,
                                card_template_id: id,
                                card_type: 'course',
                                complete_track: '0',
                            };
                        });
                        const newTrack = {
                            ...track,
                            data: {
                                ...track.data,
                                cards,
                                course_count: cards?.length || 0,
                                module_count: modules,
                                thumb_url: track.thumbnail,
                            },
                        };
                        const payload = { track: newTrack };
                        const after = {
                            type: 'POSTLIBRARYCARDTEMPLATES_REQUEST',
                            target: 'tracks',
                            payload: { data: newTrack },
                        };
                        dispatch(DevelopActions.librarytemplatesaveRequest(payload, null, 'tracks', after));
                    }
                }
                break;
            }
            case 'remove': {
                if (type === 'courses') {
                    const index = findIndex(propEq('id', item.id), template.children);
                    if (index > -1) {
                        let newModules = clone(template.data.modules);
                        let newCourses = clone(template.children);

                        newModules = remove(index, 1, newModules);
                        newCourses = remove(index, 1, newCourses);

                        const cards = newModules.map((item, key) => ({
                            blocked_by: key === 0 ? null : newModules[key - 1].card_template_id,
                            card_template_id: item.card_template_id,
                            card_type_id: item.card_type_id,
                            complete_course: 0,
                            delay_days: 0,
                        }));
                        const newCourse = {
                            ...template,
                            children: newCourses,
                            data: {
                                ...template.data,
                                modules: cards,
                                module_count: newModules?.length || 0,
                                thumb_url: template.data.thumbnail,
                            },
                        };
                        const payload = { template: newCourse };
                        const after = {
                            type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
                            target: 'courses',
                            payload: { page, perPage, search },
                        };
                        dispatch(DevelopActions.librarytemplatesaveRequest(payload, null, 'courses', after));
                    }
                }
                if (type === 'tracks') {
                    const index = findIndex(propEq('id', item.id), track.data.cards);
                    if (index > -1) {
                        let newCards = clone(track.data.cards);
                        newCards = remove(index, 1, newCards);
                        let modules = 0;
                        const cards = newCards.map(({ id, blocked_by, children }) => {
                            modules = modules + children?.length;
                            return {
                                alert_manager: '0',
                                blocked_by,
                                card_template_id: id,
                                card_type: 'course',
                                complete_track: '0',
                            };
                        });
                        const newTrack = {
                            ...track,
                            data: {
                                ...track.data,
                                cards,
                                course_count: cards?.length || 0,
                                module_count: modules,
                                thumb_url: track.thumbnail,
                            },
                        };
                        const payload = { track: newTrack };
                        const after = {
                            type: 'POSTLIBRARYCARDTEMPLATES_REQUEST',
                            target: 'tracks',
                            payload: { data: newTrack },
                        };
                        dispatch(DevelopActions.librarytemplatesaveRequest(payload, null, 'tracks', after));
                    }
                }
                break;
            }
            default:
                break;
        }
    };

    const handleShowDetail = (card, mode = 'view') => {
        const target = type === 'tracks' ? 'courses' : 'modules';
        navigate(`/library/training/${target}/${card.id}/${mode}`, { state: { template } });
    };

    const handleDeleteTemplate = (e) => {
        dispatch(DevelopActions.libraryeventRequest('template', 'delete', e.id));
    };

    const DetailComponent = {
        'tracks': TrackCard.Detail,
        'courses': CourseCard.Detail,
        'modules': ModuleCard.Detail
    }[type];

    return (
        <ErrorBoundary>
            { !template ?
                <div>{message}</div>
            : 
                <DetailComponent  data={template} onCardMenu={handleCardMenu(template)} onChildMenu={handleChildMenu} />
            }
        </ErrorBoundary>
    );
};
export default TrainingDetail;
